import { ChangeEvent, useEffect, useState } from "react";
import { Card, Col, Container, Row, Accordion, Toast, ToastContainer, Form, Alert, Stack } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { useAgentContext } from "../../context/agent";
import { useMutation, useQueryClient, useQuery } from "react-query";
import { updateAgent, updateAgentAvatar, updateAgentLauncher, addApiTool, updateApiTool, getApiTools, deleteApiTool, getSystemTools, manageSystemTools } from "../../api/agent";
import { useOrgContext } from "../../context/org";
import Chatbot from "./ChatBox";
import { FaCog, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import "./style.css";

// Import the new components
import BasicConfiguration from "./BasicConfiguration";
import AppearanceConfiguration from "./AppearanceConfiguration";
import ToolsConfiguration from "./ToolsConfiguration";
import ApiToolModal from "./ApiToolModal";
import { APITool, SystemTool } from "../../models/agent";
import SpinnerFullScreen from "../Other/SpinnerFullScreen";

function Configure() {
    const [systemPrompt, setSystemPrompt] = useState("");
    const [greeting, setGreeting] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [isPrivate, setIsPrivate] = useState(false);
    const [hidePoweredBy, setHidePoweredBy] = useState(false);
    const [useAdvancedFeatures, setUseAdvancedFeatures] = useState(false);

    const [selectedAvatarFile, setSelectedAvatarFile] = useState<File | null>(null);
    const [selectedLauncherFile, setSelectedLauncherFile] = useState<File | null>(null);

    const [primaryColor, setPrimaryColor] = useState("#212529");
    const [avatarImage, setAvatarImage] = useState("");
    const [launcherImage, setLauncherImage] = useState("");

    // Tools states
    const [showToolModal, setShowToolModal] = useState(false);
    const [apiTool, setApiTool] = useState<APITool | null>(null);

    // Toast notification state
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastType, setToastType] = useState<"success" | "error">("success");

    const [systemTools_, setSystemTools] = useState<SystemTool[]>([]);

    // Get route params first
    const { orgId, agentId } = useParams();

    const { getAgentById } = useAgentContext();
    const agent = getAgentById(agentId || "");

    const { membership, subscription } = useOrgContext();

    const allowed = ["owner", "admin", "editor"];
    const permission = !allowed.includes(membership?.role || "");

    const queryClient = useQueryClient();
    const { mutateAsync: updateAgentFn, isLoading } = useMutation(updateAgent, {
        onSuccess: (data) => {
            queryClient.invalidateQueries(["Agents", orgId]);
            showToastMessage("Configuration updated successfully!", "success");
        },
        onError: (error) => {
            showToastMessage(
                "Failed to update configuration. Please try again.",
                "error",
            );
        },
    });

    const { mutateAsync: updateAgentAvatarFn, isLoading: isLoadingAvatar } =useMutation(updateAgentAvatar, {
        onSuccess: (data) => {
            queryClient.invalidateQueries(["Agents", orgId]);
            showToastMessage("Avatar updated successfully!", "success");
        },
        onError: (error) => {
            showToastMessage(
                "Failed to update avatar. Please try again.",
                "error",
            );
        },
    });

    const { mutateAsync: updateAgentLauncherFn, isLoading: isLoadingLauncher } = useMutation(updateAgentLauncher, {
        onSuccess: (data) => {
            queryClient.invalidateQueries(["Agents", orgId]);
            showToastMessage(
                "Launcher image updated successfully!",
                "success",
            );
        },
        onError: (error) => {
            showToastMessage(
                "Failed to update launcher image. Please try again.",
                "error",
            );
        },
    });

    const handleUpdate = () => {
        if (!agent) {
            return;
        }
        updateAgentFn({
            id: agent.id,
            orgId: agent.organizationId,
            systemPrompt,
            greeting,
            displayName,
            isPrivate,
            hidePoweredBy,
            
        });
    };

    const handleLauncherFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && file.type.startsWith("image/")) {
            setSelectedLauncherFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLauncherImage(reader.result as string);
            };
            reader.readAsDataURL(file);
        } else {
            alert("Please select a valid image file.");
        }
    };

    const handleAvatarFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && file.type.startsWith("image/")) {
            setSelectedAvatarFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatarImage(reader.result as string);
            };
            reader.readAsDataURL(file);
        } else {
            alert("Please select a valid image file.");
        }
    };

    const updateAppearance = () => {
        if (!agent) {
            return;
        }

        try {
            if (primaryColor && primaryColor !== agent.primaryColor) {
                updateAgentFn({
                    id: agent.id,
                    orgId: agent.organizationId,
                    primaryColor,
                });
            }

            if (selectedAvatarFile) {
                const formData = new FormData();
                formData.append("agentId", agent.id);
                formData.append("orgId", agent.organizationId);
                formData.append("file", selectedAvatarFile);
                updateAgentAvatarFn({
                    orgId: agent.organizationId,
                    id: agent.id,
                    formData,
                });
                setSelectedAvatarFile(null);
            }

            if (selectedLauncherFile) {
                const formData = new FormData();
                formData.append("agentId", agent.id);
                formData.append("orgId", agent.organizationId);
                formData.append("file", selectedLauncherFile);
                updateAgentLauncherFn({
                    orgId: agent.organizationId,
                    id: agent.id,
                    formData,
                });
                setSelectedLauncherFile(null);
            }

            // Show success message if no files were uploaded (only color change)
            if (
                !selectedAvatarFile &&
                !selectedLauncherFile &&
                primaryColor &&
                primaryColor !== agent.primaryColor
            ) {
                showToastMessage(
                    "Primary color updated successfully!",
                    "success",
                );
            }
        } catch (error) {
            showToastMessage(
                "Failed to update appearance. Please try again.",
                "error",
            );
        }
    };

    /** Tools Management */

    // Fetch system tools from API
    const {  data: systemTools = [],} = useQuery(
        ["SystemTools", agentId],
        () => getSystemTools(orgId!, agentId!),
        {
            enabled: !!orgId && !!agentId,
        },
    );

    // Fetch API tools from API
    const { data: apiTools = [], } = useQuery(
        ["ApiTools", agentId],
        () => getApiTools(orgId!, agentId!),
        {
            enabled: !!orgId && !!agentId,
        },
    );

    // Mutation for activating system tools
    const { mutateAsync: manageSystemToolsFn } = useMutation(
        manageSystemTools,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(["Agents", orgId]);
                showToastMessage("Tool updated successfully!", "success");
            },
            onError: (error) => {
                showToastMessage(
                    "Failed to update tool. Please try again.",
                    "error",
                );
            },
        },
    );

    // Mutations for API tools CRUD operations
    const { mutateAsync: addApiToolFn, isLoading: isAddingApiTool } = useMutation(
        addApiTool,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(["ApiTools", agentId]);
                // queryClient.invalidateQueries(["Agents", orgId]);
                showToastMessage("API tool added successfully!", "success");
                setShowToolModal(false);
                setApiTool(null);
            },
            onError: (error) => {
                showToastMessage(
                    "Failed to add API tool. Please try again.",
                    "error",
                );
            },
        },
    );

    const { mutateAsync: updateApiToolFn, isLoading: isUpdatingApiTool } = useMutation(
        updateApiTool,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(["ApiTools", agentId]);
                // queryClient.invalidateQueries(["Agents", orgId]);
                showToastMessage("API tool updated successfully!", "success");
                setShowToolModal(false);
                setApiTool(null);
            },
            onError: (error) => {
                showToastMessage(
                    "Failed to update API tool. Please try again.",
                    "error",
                );
            },
        },
    );

    const { mutateAsync: deleteApiToolFn, isLoading: isDeletingApiTool } = useMutation(
        deleteApiTool,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(["ApiTools", agentId]);
                queryClient.invalidateQueries(["Agents", orgId]);
                showToastMessage("API tool deleted successfully!", "success");
            },
            onError: (error) => {
                showToastMessage(
                    "Failed to delete API tool. Please try again.",
                    "error",
                );
            },
        },
    );

    const handleToggleSystemTool = async (tool: SystemTool) => {
        if (!orgId || !agentId) return;

        try {
            await manageSystemToolsFn({
                orgId,
                agentId,
                toolId: tool.id,
                enabled: !tool.enabled,
            });
        } catch (error) {
            console.error("Failed to toggle system tool:", error);
        }
    };

    const handleToggleApiTool = async (tool: APITool) => {
        if (!orgId || !agentId) return;

        try {
            
            await updateApiToolFn({
                orgId,
                agentId,
                id: tool.id,
                tool: {
                    ...tool,
                    enabled: !tool.enabled,
                },
            });
        } catch (error) {
            console.error("Failed to toggle API tool:", error);
        }
    };

    const handleCreateApiTool = async (tool: Omit<APITool, "id" | "createdAt" | "updatedAt">) => {
        if (!tool.url) {
            showToastMessage("Please provide tool name and URL.", "error");
            return;
        }
        if (!orgId || !agentId) return;

        try {
            await addApiToolFn({ orgId, agentId, tool });
        } catch (error) {
            console.error("Failed to add API tool:", error);
        }
    };

    const handleUpdateApiTool = async (tool: APITool) => {
        if (!tool.id || !tool.url) {
            showToastMessage("Please provide tool name and URL.", "error");
            return;
        }
        if (!orgId || !agentId) return;

        try {
            await updateApiToolFn({ orgId, agentId, id: tool.id, tool });
        } catch (error) {
            console.error("Failed to update API tool:", error);
        }
    };

    const handleDeleteApiTool = async (toolId: string) => {
        if (!orgId || !agentId) return;

        try {
            await deleteApiToolFn({
                orgId,
                agentId,
                toolId,
            });
        } catch (error) {
            console.error("Failed to delete API tool:", error);
        }
    };

    const handleUseAdvancedFeaturesToggle = (e: ChangeEvent<HTMLInputElement>) => {
        const isEnabled = e.target.checked;
        setUseAdvancedFeatures(isEnabled);
        if (!agent) {
            return;
        }
        updateAgentFn({
            id: agent.id,
            orgId: agent.organizationId,
            useAdvancedFeatures: isEnabled,
        });
    }

    // API Tools event handlers
    const handleEditApiTool = (tool: APITool) => {
        setApiTool(tool);
        setShowToolModal(true);
    };

    const handleAddApiTool = () => {
        setApiTool(null);
        setShowToolModal(true);
    };

    const handleCloseModal = () => {
        setShowToolModal(false);
        setApiTool(null);
    };

    // Toast notification helper
    const showToastMessage = (message: string, type: "success" | "error") => {
        setToastMessage(message);
        setToastType(type);
        setShowToast(true);
    };

    useEffect(() => {
        if (agent) {
            setSystemPrompt(agent.systemPrompt);
            setGreeting(agent.greeting);
            setDisplayName(agent.displayName);
            setIsPrivate(agent.isPrivate);
            setHidePoweredBy(agent.hidePoweredBy);
            setUseAdvancedFeatures(agent.useAdvancedFeatures);

            setPrimaryColor(agent.primaryColor);
            setAvatarImage(agent.avatarImageURL || "");
            setLauncherImage(agent.launcherImageURL || "");
        }
    }, [agent]);

    useEffect(() => {
        const updatedSystemTools = systemTools.map(tool => {
            return {
                ...tool,
                enabled: agent?.systemTools.includes(tool.id) || false,
            };
        });
        setSystemTools(updatedSystemTools);
    }, [agent, systemTools]);

    return (
        <>
            {(isAddingApiTool || isUpdatingApiTool || isDeletingApiTool) && <SpinnerFullScreen/>}
            {!agent ? (
                <Container style={{ minHeight: "100vh", padding: "2rem 0" }}>
                    <div
                        style={{
                            backgroundColor: "#2d2d2d",
                            padding: "3rem",
                            borderRadius: "8px",
                            textAlign: "center",
                        }}
                    >
                        <h1 style={{ color: "#fff" }}>Agent not found</h1>
                    </div>
                </Container>
            ) : (
                <div style={{ minHeight: "100vh" }}>
                    {/* Header */}
                    <div
                        className="sticky-top"
                        style={{
                            backgroundColor: "#2d2d2d",
                            borderBottom: "1px solid #444",
                            padding: "1rem 0",
                        }}
                    >
                        <Container>
                            <div className="d-flex align-items-center">
                                <div className="bg-primary rounded p-2 me-3">
                                    <FaCog className="text-white" />
                                </div>
                                <div>
                                    <h4 className="mb-0 text-light">
                                        Configure Agent
                                    </h4>
                                    <small style={{ color: "#999" }}>
                                        Customize your AI agent's behavior,
                                        appearance, and tools
                                    </small>
                                </div>
                            </div>
                        </Container>
                    </div>

                    <Container fluid className="py-4">
                        <Row>
                            <Col lg={6} className="mb-4">
                                {/* Accordions for organized content */}
                                <Accordion
                                    defaultActiveKey={["0"]}
                                    alwaysOpen
                                    style={{ marginBottom: "1.5rem" }}
                                >
                                    {/* Basic Configuration */}
                                    <BasicConfiguration
                                        displayName={displayName}
                                        setDisplayName={setDisplayName}
                                        greeting={greeting}
                                        setGreeting={setGreeting}
                                        systemPrompt={systemPrompt}
                                        setSystemPrompt={setSystemPrompt}
                                        hidePoweredBy={hidePoweredBy}
                                        setHidePoweredBy={setHidePoweredBy}
                                        permission={permission}
                                        subscription={subscription}
                                        orgId={orgId!}
                                        isLoading={isLoading}
                                        onUpdate={handleUpdate}
                                    />

                                    {/* Appearance */}
                                    <AppearanceConfiguration
                                        primaryColor={primaryColor}
                                        setPrimaryColor={setPrimaryColor}
                                        avatarImage={avatarImage}
                                        setAvatarImage={setAvatarImage}
                                        launcherImage={launcherImage}
                                        setLauncherImage={setLauncherImage}
                                        selectedAvatarFile={selectedAvatarFile}
                                        setSelectedAvatarFile={setSelectedAvatarFile}
                                        selectedLauncherFile={selectedLauncherFile}
                                        setSelectedLauncherFile={setSelectedLauncherFile}
                                        handleAvatarFileChange={handleAvatarFileChange}
                                        handleLauncherFileChange={handleLauncherFileChange}
                                        agent={agent}
                                        permission={permission}
                                        isLoadingAvatar={isLoadingAvatar}
                                        isLoadingLauncher={isLoadingLauncher}
                                        onUpdateAppearance={updateAppearance}
                                    />

                                    <Card
                                        className="mb-3"
                                        style={{
                                            backgroundColor: "#2d2d2d",
                                            border: "1px solid #444",
                                            // color: "#fff",
                                        }}
                                    >
                                        <Card.Header className="text-light fw-bold">
                                            <div className="d-flex align-items-center">
                                                <div className="bg-info rounded p-2 me-3">
                                                    <FaCog className="text-white" />
                                                </div>
                                                <div>
                                                    <span className="text-light fw-bold">Advanced Features</span>
                                                    <br />
                                                    <small style={{ color: '#999' }}>Tools and Agentic AI</small>
                                                </div>
                                            </div>
                                        </Card.Header>
                                        <Card.Body>
                                            <Form.Group className="mb-3">
                                                <Form.Check
                                                    disabled={subscription && ["Free", "Starter"].includes(subscription.name)}
                                                    type="switch"
                                                    id="custom-switch"
                                                    label="Use Advanced Features"
                                                    checked={useAdvancedFeatures}
                                                    onChange={e => handleUseAdvancedFeaturesToggle(e)}
                                                    style={{ color: '#ccc' }}
                                                />
                                                <small style={{ color: "#ccc" }}>
                                                    Advanced features allow your agent to use system tools, add custom API integrations, and leverage agentic AI (beyond basic RAG).<br/> 
                                                    <span className="fw-bold">Note: Advanced features consume more credits per interaction</span>.
                                                </small>
                                                {subscription && ["Free", "Starter"].includes(subscription.name) && (
                                                    <Alert 
                                                        variant="warning" 
                                                        className="mt-2"
                                                        style={{ backgroundColor: '#2d2a1a', borderColor: '#ffc107', color: '#fff3cd' }}
                                                        >
                                                        <Link to={`/dashboard/orgs/${orgId}/plans`} style={{ color: '#ffc107' }}>
                                                            Upgrade your subscription to enable advanced features.
                                                        </Link>
                                                    </Alert>
                                                )}
                                            </Form.Group>
                                        </Card.Body>
                                    </Card>

                                    {/* Tools & Integrations */}
                                    {agent.useAdvancedFeatures && <ToolsConfiguration
                                        systemTools={systemTools_}
                                        apiTools={apiTools}
                                        permission={permission}
                                        onToggleSystemTool={handleToggleSystemTool}
                                        onToggleApiTool={handleToggleApiTool}
                                        onAddApiTool={handleAddApiTool}
                                        onEditApiTool={handleEditApiTool}
                                        onDeleteApiTool={handleDeleteApiTool}
                                    />}
                                </Accordion>
                            </Col>

                            {/* Chatbot Preview */}
                            <Col lg={6}>
                                <div
                                    className="sticky-top"
                                    style={{ top: "6rem" }}
                                >
                                    <Card
                                        style={{
                                            backgroundColor: "#2d2d2d",
                                            borderColor: "#444",
                                        }}
                                    >
                                        <Card.Header
                                            style={{
                                                backgroundColor: "#2d2d2d",
                                                borderBottom: "1px solid #444",
                                            }}
                                        >
                                            <h5 className="mb-0 text-light">
                                                Live Preview
                                            </h5>
                                            <small style={{ color: "#999" }}>
                                                See how your agent will look and
                                                behave
                                            </small>
                                        </Card.Header>
                                        <Card.Body
                                            style={{
                                                // minHeight: "400px",
                                                // display: "flex",
                                                // alignItems: "center",
                                                // justifyContent: "center",
                                            }}
                                        >
                                            <Chatbot
                                                orgId={agent.organizationId}
                                                agentId={agent.id}
                                                displayName={displayName}
                                                greeting={greeting}
                                                primaryColor={primaryColor}
                                                launcherImageURL={launcherImage}
                                                avatarImageURL={avatarImage}
                                                hidePoweredBy={hidePoweredBy}
                                            />
                                            {/* <Stack style={{minHeight: '300px'}}></Stack> */}
                                        </Card.Body>
                                    </Card>
                                </div>
                            </Col>
                        </Row>
                    </Container>

                    {/* API Tool Modal */}
                    <ApiToolModal
                        show={showToolModal}
                        apiTool={apiTool}
                        onHide={handleCloseModal}
                        onSave={handleCreateApiTool}
                        onUpdate={handleUpdateApiTool}
                    />

                    {/* Toast Notifications */}
                    <ToastContainer
                        position="top-end"
                        style={{
                            position: "fixed",
                            top: "20px",
                            right: "20px",
                            zIndex: 9999,
                        }}
                    >
                        <Toast
                            show={showToast}
                            onClose={() => setShowToast(false)}
                            delay={4000}
                            autohide
                            style={{
                                backgroundColor:
                                    toastType === "success"
                                        ? "#1a2e1a"
                                        : "#2e1a1a",
                                borderColor:
                                    toastType === "success"
                                        ? "#28a745"
                                        : "#dc3545",
                            }}
                        >
                            <Toast.Header
                                style={{
                                    backgroundColor:
                                        toastType === "success"
                                            ? "#1a2e1a"
                                            : "#2e1a1a",
                                    borderBottom: `1px solid ${toastType === "success" ? "#28a745" : "#dc3545"}`,
                                    color: "#fff",
                                }}
                            >
                                {toastType === "success" ? (
                                    <FaCheckCircle className="text-success me-2" />
                                ) : (
                                    <FaExclamationTriangle className="text-danger me-2" />
                                )}
                                <strong className="me-auto text-light">
                                    {toastType === "success"
                                        ? "Success"
                                        : "Error"}
                                </strong>
                            </Toast.Header>
                            <Toast.Body className="text-light">
                                {toastMessage}
                            </Toast.Body>
                        </Toast>
                    </ToastContainer>
                </div>
            )}
        </>
    );
}

export default Configure;
